<template>
  <div
    v-show="show"
    :data-testid="dataTestid"
    :class="[
      component.main,
      mobileFullScreen ? 'v2-modal--mobile-full-screen max-sm:!bg-transparent max-sm:!p-0' : ''
    ]"
    :style="mobileFullScreen ? {
      '--pwa-nav-offset': pwaNavOffset
    } : undefined"
    class="modal-backdrop left-0 top-0 wh-full flex p-4 md:p-6 justify-center-safe bg-base-neutral-neutral/80 overflow-auto"
  >
    <V2AtomsBaseCard
      v-show="show"
      :id="component.id"
      v-click-outside="onClickOutside"
      :wrapper-class="`card max-w-[512px] relative z-10 flex flex-col gap-4 ${mobileFullScreenCardClass} ${props.cardClass}`"
      :size="component.cardSize"
      :class="closeReserveClass"
    >
      <button
        v-if="enableCloseIcon"
        class="v2-modal__close absolute right-3 top-3"
        :class="[mobileFullScreenCloseClass, closeIconClass]"
        @click="closeButtonAction"
      >
        <V2IconsClose
          :width="closeSizes[closeButtonSize]?.width ?? ''"
          :height="closeSizes[closeButtonSize]?.height ?? ''"
          :fill="semanticColorLookup(props.closeIconColor)"
        />
      </button>

      <V2IconsCheck
        v-if="showAsset"
        width="80"
        height="80"
        :fill="semanticColorLookup('primary.default')"
      />

      <div class="flex flex-col gap-4">
        <div
          v-if="$slots.header"
          class="text-semantic-surface-on-surface font-bold text-2xl"
        >
          <slot name="header" />
        </div>

        <div>
          <slot name="body" />
        </div>
      </div>

      <div
        v-if="props.ctaButtons && props.ctaButtons.length"
        class="pt-2"
        :class="component.ctaWrapper"
      >
        <template v-for="(button, index) in props.ctaButtons" :key="index">
          <V2AtomsButton
            class="w-full"
            :theme="button.theme"
            :size="button.size"
            @click="button.callback"
          >
            {{ button.text }}
          </V2AtomsButton>
        </template>
      </div>

      <div
        v-if="$slots.disclaimer"
        class="text-semantic-surface-on-surface-variant pt-6 border-t-1 border-semantic-outline-variant text-sm"
      >
        <slot name="disclaimer" />
      </div>
    </V2AtomsBaseCard>
  </div>
</template>

<script setup>
import V2IconsClose from '~/components/v2/icons/Close.vue'
import V2IconsCheck from '~/components/v2/icons/Check.vue'

const { devices } = deviceChecker()

defineOptions({
  name: 'V2MoleculesModal'
})

const props = defineProps({
  dataTestid: {
    type: String,
    default: ''
  },

  cardSize: {
    type: String,
    default: 'small',
    validator: value => ['small', 'base', 'large'].includes(value)
  },

  cardClass: {
    type: String,
    default: ''
  },

  fullPage: {
    type: Boolean,
    default: true
  },

  // Opt-in mobile full-screen treatment (default off so existing consumers are
  // unaffected). When enabled, on mobile (< sm) the modal fills the space between
  // the fixed dashboard header and the PWA bottom nav — reading as a full page
  // with the dashboard surface colour — while desktop keeps the centred card.
  mobileFullScreen: {
    type: Boolean,
    default: false
  },

  overlayCustomClass: {
    type: String,
    default: ''
  },

  enableCloseIcon: {
    type: Boolean,
    default: true
  },

  overlayClickable: {
    type: Boolean,
    default: true
  },

  closeAction: {
    type: Function,
    default: event => event
  },

  closeButtonSize: {
    type: String,
    default: 'base'
  },

  closeIconClass: {
    type: String,
    default: ''
  },

  closeReserveClass: {
    type: [String, Object],
    default: ''
  },

  showAsset: {
    type: Boolean,
    default: true
  },

  ctaButtons: {
    type: Array,
    default: () => [
      {
        theme: 'primary',
        size: 'large',
        text: 'This Is A Button',
        callback: () => {
          // TODO: Add callback
        }
      },
      {
        theme: 'secondary',
        size: 'large',
        text: 'This Is A Button',
        callback: () => {
          // TODO: Add callback
        }
      }
    ],
    validator: value => {
      return value.length <= 2
    }
  },

  isStack: {
    type: Boolean,
    default: true
  },

  closeIconColor: {
    type: String,
    default: 'primary.default'
  }
})

const closeSizes = {
  base: {
    width: '24',
    height: '24'
  }
}

const show = ref(false)
const modalCardHeight = ref()
const modalId = `modal-${useId()}`

// ── Mobile full-screen variant (opt-in via `mobileFullScreen`) ──────────────
// Ported from ReviewStatusModal: on mobile the modal fills the space between the
// fixed header and the PWA bottom nav (when the page shows one). Colours/sizing are
// applied via the semantic utilities below; positioning offsets are supplied as
// inline CSS vars.
const pwaNavOffset = ref('0px')

const mobileFullScreenCardClass = computed(() =>
  props.mobileFullScreen
    ? 'max-sm:!w-full max-sm:!max-w-none max-sm:!min-h-full max-sm:!border-0 max-sm:!rounded-none max-sm:!shadow-none max-sm:!bg-semantic-surface-container-variant-low'
    : ''
)

// Background for the sticky close bar in the mobile full-screen variant — matches
// the panel so content scrolling beneath it is masked. Layout (sticky/full-width/
// right-align) lives in the scoped <style> block.
const mobileFullScreenCloseClass = computed(() =>
  props.mobileFullScreen ? 'max-sm:!bg-semantic-surface-container-variant-low' : ''
)

// Top padding reserved for the absolute close icon. The mobile full-screen variant
// replaces the absolute button with an in-flow sticky bar, so the reserve is only
// needed on desktop (>= sm) there; other consumers keep it at all breakpoints.
const closeReserveClass = computed(() => {
  if (props.closeReserveClass !== '') return props.closeReserveClass

  if (props.showAsset || !props.enableCloseIcon) return ''

  return props.mobileFullScreen ? 'sm:!pt-10' : '!pt-10'
})

const component = computed(() => {
  const id = modalId

  const main = (() => {
    const alignment = modalCardHeight.value >= devices.height.value
      ? 'items-start-safe'
      : 'items-center-safe'

    const temp = props.fullPage
      ? 'fixed z-[99]'
      : 'absolute z-[1]'

    return `${alignment} ${temp}`
  })()

  const ctaWrapper = (() => {
    if (props.ctaButtons.length === 2 && props.isStack) {
      return 'flex flex-col gap-2'
    }

    return 'flex flex-row gap-2'
  })()

  return {
    id,
    main,
    cardSize: props.cardSize,
    ctaWrapper
  }
})

onBeforeUnmount(() => {
  toggle(false)

  const body = document.querySelector('body')
  body.style.overflow = ''
})

const emit = defineEmits([
  'toggle'
])

const toggle = value => {
  if (typeof value === 'undefined') {
    show.value = !show.value
  } else {
    show.value = value
  }
  emit('toggle', show)
}

const closeButtonAction = () => {
  props.closeAction()
  toggle(false)
}

const onClickOutside = event => {
  // Accept clicks only from the modal backdrop
  const isClickOutside = event?.target?.classList.contains('modal-backdrop')
  if (!isClickOutside || !props.overlayClickable) {
    return
  }
  toggle(false)
}

watch(show, async value => {
  // Mobile full-screen: stop above the app's bottom nav only when the page shows
  // one, measured as the modal opens (pwaNavBarOffset).
  if (value && props.mobileFullScreen) pwaNavOffset.value = pwaNavBarOffset()

  const body = document.querySelector('body')
  if (value) {
    body.style.overflow = 'hidden'
  } else {
    body.style.overflow = ''
  }

  await nextTick()

  const el = document.getElementById(component.value.id)

  if (!modalCardHeight.value && el) {
    modalCardHeight.value = el.getBoundingClientRect()?.height
  }
})

defineExpose({
  show: show.value,
  toggle
})
</script>

<style scoped>
/*
 * Mobile full-screen variant (opt-in via `mobileFullScreen`).
 *
 * Repositions the overlay to sit between the fixed dashboard header and the PWA
 * bottom nav so the modal reads as a full page. Colours, background and card
 * sizing are applied via semantic Tailwind utilities in the template; this block
 * only owns the structural positioning that depends on runtime CSS vars.
 *
 * --smart-app-banner-height is published on :root by V2AtomsSmartAppBanner (0px
 * when there is no banner); --pwa-nav-offset reserves the PWA bottom-nav height
 * and is supplied inline. Desktop (>= sm / 640px) keeps the centred card.
 */
.v2-modal--mobile-full-screen {
  --pwa-nav-offset: 0px;
}

@media (max-width: 639px) {
  .modal-backdrop.v2-modal--mobile-full-screen {
    position: fixed;
    left: 0;
    right: 0;
    width: 100%;
    height: auto;
    /* Matches the bottom edge of the fixed header: 4rem of content, its pt-safe
       padding, and the Smart App Banner it is pushed down by. */
    top: calc(4rem + env(safe-area-inset-top, 0px) + var(--smart-app-banner-height));
    bottom: var(--pwa-nav-offset, 0px);
    /* Animate in sync with the header, which transitions `top` over 300ms when the
       Smart App Banner shows/dismisses (see layouts/memberhub.vue). */
    transition: top 0.3s ease-in-out;
  }

  /* Close icon becomes a full-width bar that sticks to the top of the scrolling
     panel, with the icon right-aligned. Negative margins bleed over the card's
     p-4 so the bar spans edge-to-edge and sits flush at the top; its background
     (semantic class in the template) masks content scrolling beneath. */
  .modal-backdrop.v2-modal--mobile-full-screen .v2-modal__close {
    position: sticky;
    top: 0;
    z-index: 20;
    display: flex;
    justify-content: flex-end;
    margin: -1rem -1rem 0;
    padding: 0.75rem 1rem;
  }
}
</style>
